import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import Modal from "@/components/Modal";
import {
  FileText, Download, Upload, Trash2, Eye, Plus, AlertTriangle, CheckCircle2,
  FileSignature, X, Loader2,
} from "lucide-react";

const TYPE_LABELS = { conditional: "Conditional", final: "Final" };
const REQUIRED_HINT = [
  "{{candidate_full_name}}", "{{candidate_first_name}}",
  "{{salary}}", "{{start_date}}",
];

export default function OfferTemplatesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [previewing, setPreviewing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/offer-templates");
      setItems(data || []);
    } catch (e) {
      toast.error("Failed to load templates");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const conditional = useMemo(() => items.filter((t) => t.type === "conditional"), [items]);
  const final = useMemo(() => items.filter((t) => t.type === "final"), [items]);

  const downloadSample = async (sampleType = "conditional") => {
    try {
      const res = await api.get(`/offer-templates/sample?type=${sampleType}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Rabah_Concept_Sample_${sampleType[0].toUpperCase() + sampleType.slice(1)}_Offer_Letter_Template.docx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${sampleType[0].toUpperCase() + sampleType.slice(1)} sample downloaded`);
    } catch (e) {
      toast.error("Sample download failed");
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/offer-templates/${t.id}`);
      toast.success("Template deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="fade-up">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-eyebrow mb-2">Recruitment</div>
          <h1 className="font-display text-4xl font-bold flex items-center gap-3">
            <FileSignature className="w-8 h-8" /> Offer Letter Templates
          </h1>
          <p className="text-[var(--cc-text-soft)] mt-1">
            Custom Word (.docx) templates with Jinja2 merge fields. Upload, preview, and generate populated PDF offers per candidate.
          </p>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary inline-flex items-center gap-1.5" onClick={() => downloadSample("conditional")} data-testid="download-sample-template-conditional">
            <Download className="w-4 h-4" /> Conditional sample
          </button>
          <button className="btn-secondary inline-flex items-center gap-1.5" onClick={() => downloadSample("final")} data-testid="download-sample-template-final">
            <Download className="w-4 h-4" /> Final sample
          </button>
          <button className="btn-primary inline-flex items-center gap-1.5" onClick={() => setShowUpload(true)} data-testid="upload-template-button">
            <Plus className="w-4 h-4" /> Upload template
          </button>
        </div>
      </div>

      <RequiredFieldsHint />

      <Section title="Conditional Offer Templates" rows={conditional} loading={loading} kind="conditional"
               onPreview={setPreviewing} onDelete={remove} />
      <Section title="Final Offer Templates"       rows={final}       loading={loading} kind="final"
               onPreview={setPreviewing} onDelete={remove} />

      {showUpload && <UploadModal onClose={() => setShowUpload(false)} onSaved={() => { setShowUpload(false); load(); }} />}
      {previewing && <PreviewModal template={previewing} onClose={() => setPreviewing(null)} />}
    </div>
  );
}

function RequiredFieldsHint() {
  return (
    <div className="cc-card p-4 mb-6 bg-[var(--cc-surface)] border-l-4 border-[var(--cc-sage)]" data-testid="required-fields-hint">
      <div className="flex items-start gap-3">
        <CheckCircle2 className="w-5 h-5 text-[var(--cc-sage)] shrink-0 mt-0.5" />
        <div className="text-sm leading-relaxed">
          <div className="font-semibold mb-1">Every template must include these merge fields:</div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {REQUIRED_HINT.map((f) => (
              <code key={f} className="text-[11px] px-1.5 py-0.5 rounded bg-white border border-[var(--cc-border)]">{f}</code>
            ))}
          </div>
          <div className="text-[var(--cc-text-soft)] text-xs">
            Conditional offers also require at least one of:{" "}
            <code className="text-[11px] px-1 bg-white">{"{{dbs_deadline}}"}</code>,{" "}
            <code className="text-[11px] px-1 bg-white">{"{{references_deadline}}"}</code>,{" "}
            <code className="text-[11px] px-1 bg-white">{"{{rtw_deadline}}"}</code>.
            Download the sample to see the full list of supported fields.
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, rows, loading, kind, onPreview, onDelete }) {
  return (
    <div className="mb-8" data-testid={`offer-templates-${kind}-section`}>
      <h2 className="font-display text-xl font-bold mb-3">{title} <span className="opacity-50 text-base font-normal">({rows.length})</span></h2>
      <div className="cc-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--cc-surface)]">
            <tr className="text-left">
              <th className="px-5 py-3 label-eyebrow">Template name</th>
              <th className="px-5 py-3 label-eyebrow">Uploaded</th>
              <th className="px-5 py-3 label-eyebrow">Size</th>
              <th className="px-5 py-3 label-eyebrow">Merge fields</th>
              <th className="px-5 py-3 label-eyebrow text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="5" className="text-center py-10 text-[var(--cc-text-soft)]">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan="5" className="text-center py-10 text-[var(--cc-text-soft)]" data-testid={`empty-${kind}`}>
                No {kind} templates yet — click "Upload template" to add one.
              </td></tr>
            )}
            {rows.map((t) => (
              <tr key={t.id} className="border-t border-[var(--cc-border)]" data-testid={`offer-template-row-${t.id}`}>
                <td className="px-5 py-3">
                  <div className="font-medium flex items-center gap-2">
                    <FileText className="w-4 h-4 opacity-50" />
                    {t.name}
                    <span className={`pill !text-[9px] ${t.type === "final" ? "pill-info" : "pill-warning"}`} data-testid={`template-type-pill-${t.id}`}>
                      {t.type === "final" ? "FINAL" : "CONDITIONAL"}
                    </span>
                  </div>
                  <div className="text-[11px] text-[var(--cc-text-soft)] mt-0.5">by {t.uploaded_by_name || "—"}</div>
                </td>
                <td className="px-5 py-3 text-xs text-[var(--cc-text-soft)] whitespace-nowrap">
                  {t.uploaded_at ? new Date(t.uploaded_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-5 py-3 text-xs text-[var(--cc-text-soft)] whitespace-nowrap">
                  {(t.file_size / 1024).toFixed(1)} KB
                </td>
                <td className="px-5 py-3">
                  <span className="pill pill-sage !text-[10px]" data-testid={`merge-field-count-${t.id}`}>
                    {t.merge_fields?.length || 0} fields
                  </span>
                </td>
                <td className="px-5 py-3 text-right">
                  <button className="btn-ghost p-1.5" title="Preview" onClick={() => onPreview(t)} data-testid={`preview-template-${t.id}`}>
                    <Eye className="w-4 h-4" />
                  </button>
                  <button className="btn-ghost p-1.5" title="Delete" onClick={() => onDelete(t)} data-testid={`delete-offer-template-${t.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UploadModal({ onClose, onSaved }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("conditional");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState(null);

  const submit = async () => {
    if (!name.trim()) return toast.error("Template name is required");
    if (!file) return toast.error("Select a DOCX file");
    if (!file.name.toLowerCase().endsWith(".docx")) return toast.error("Only DOCX files are accepted");
    if (file.size > 10 * 1024 * 1024) return toast.error("File exceeds 10 MB");

    setErrors(null); setBusy(true);
    try {
      const form = new FormData();
      form.append("template_name", name.trim());
      form.append("template_type", type);
      form.append("file", file);
      const { data } = await api.post("/offer-templates/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Template "${data.name}" uploaded · ${data.merge_fields.length} merge fields detected`);
      onSaved();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object") {
        setErrors(detail);
        toast.error(detail.message || "Validation failed");
      } else {
        toast.error(detail || "Upload failed");
      }
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} testId="upload-template-modal" widthClass="max-w-xl">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-2xl font-bold flex items-center gap-2">
          <Upload className="w-6 h-6" /> Upload Offer Letter Template
        </h2>
        <button className="btn-ghost" onClick={onClose}><X className="w-4 h-4" /></button>
      </div>

      <div className="space-y-4">
        <div>
          <label className="label-eyebrow block mb-1">Template name *</label>
          <input
            className="cc-input" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Standard Conditional Offer 2026"
            data-testid="upload-template-name"
          />
        </div>

        <div>
          <label className="label-eyebrow block mb-1">Template type *</label>
          <div className="flex gap-2">
            {["conditional", "final"].map((t) => (
              <button key={t}
                onClick={() => setType(t)}
                className={`flex-1 py-2 rounded-lg border text-sm font-medium transition ${
                  type === t ? "bg-[var(--cc-sage)] text-white border-[var(--cc-sage)]" : "bg-white border-[var(--cc-border)] hover:bg-[var(--cc-surface)]"
                }`}
                data-testid={`upload-template-type-${t}`}
              >
                {TYPE_LABELS[t]} Offer
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="label-eyebrow block mb-1">DOCX file * <span className="opacity-60">(max 10 MB)</span></label>
          <input
            type="file" accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setErrors(null); }}
            className="block w-full text-sm file:mr-3 file:px-4 file:py-2 file:rounded-lg file:border-0 file:bg-[var(--cc-sage)] file:text-white file:font-medium hover:file:bg-[var(--cc-sage-deep)]"
            data-testid="upload-template-file"
          />
          {file && <div className="text-[11px] text-[var(--cc-text-soft)] mt-1">{file.name} · {(file.size / 1024).toFixed(1)} KB</div>}
        </div>

        {errors && (
          <div className="bg-[#fdf3ec] border-l-4 border-[var(--cc-accent)] p-3 rounded text-sm" data-testid="upload-template-errors">
            <div className="font-medium flex items-center gap-1.5 mb-2"><AlertTriangle className="w-4 h-4" /> {errors.message}</div>
            {errors.missing_required?.length > 0 && (
              <div className="text-xs mb-1">
                <span className="font-medium">Missing required fields:</span>{" "}
                {errors.missing_required.map((f) => <code key={f} className="mx-0.5 px-1 bg-white">{f}</code>)}
              </div>
            )}
            {errors.missing_deadlines?.length > 0 && (
              <div className="text-xs mb-1">
                <span className="font-medium">Conditional offers need at least one deadline:</span>{" "}
                {errors.missing_deadlines.map((f) => <code key={f} className="mx-0.5 px-1 bg-white">{f}</code>)}
              </div>
            )}
            {errors.detected_fields?.length > 0 && (
              <div className="text-[11px] text-[var(--cc-text-soft)] mt-1">
                Detected fields in your file: {errors.detected_fields.join(", ")}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-6 flex justify-end gap-2">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary inline-flex items-center gap-1.5" onClick={submit} disabled={busy} data-testid="upload-template-submit">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4" /> Upload & Validate</>}
        </button>
      </div>
    </Modal>
  );
}

function PreviewModal({ template, onClose }) {
  const [paragraphs, setParagraphs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    api.post(`/offer-templates/${template.id}/preview`, {
      // synthetic example candidate + offer payload — gives HR Admin a sense of
      // what the populated letter will look like before they pick a real candidate
      offer: {
        salary: 24500,
        start_date: "2026-06-01",
        dbs_deadline: "2026-07-01",
        references_deadline: "2026-06-22",
        rtw_deadline: "2026-06-15",
        medical_deadline: "2026-07-01",
      },
    }).then(({ data }) => {
      if (cancelled) return;
      setParagraphs(data.preview_paragraphs || []);
    }).catch((e) => {
      if (cancelled) return;
      setError(e.response?.data?.detail || "Preview failed");
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [template.id]);

  return (
    <Modal onClose={onClose} testId="preview-template-modal" widthClass="max-w-3xl">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="font-display text-2xl font-bold flex items-center gap-2">
            <Eye className="w-6 h-6" /> Template preview
          </h2>
          <div className="text-xs text-[var(--cc-text-soft)] mt-1">
            <strong>{template.name}</strong> · {TYPE_LABELS[template.type]} offer · using a synthetic example candidate
          </div>
        </div>
        <button className="btn-ghost" onClick={onClose}><X className="w-4 h-4" /></button>
      </div>

      <div className="border border-[var(--cc-border)] rounded-lg bg-white p-6 max-h-[60vh] overflow-y-auto" data-testid="preview-content">
        {loading && <div className="text-center py-10 text-[var(--cc-text-soft)]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Rendering preview…</div>}
        {error && <div className="text-[var(--cc-accent)] text-sm py-2" data-testid="preview-error">{error}</div>}
        {!loading && !error && paragraphs.map((p, i) => (
          <p key={i} className="mb-2 text-sm leading-relaxed whitespace-pre-wrap">{p}</p>
        ))}
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <button className="btn-primary" onClick={onClose}>Close</button>
      </div>
    </Modal>
  );
}
